import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryOffline } from 'hooks';
import { MobileAppValuesContext } from 'contexts';
import {
  MOBILE_APP_STARTUP_COMPLETION_SUPPORT_FLAG,
  MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT,
} from 'constants';
import ANNOTATIONS_IN_DOCUMENT_QUERY from 'graphql/queries/userContent/EpubAnnotationsInDocumentQuery.graphql';
import ANNOTATIONS_FROM_SHARES_IN_DOCUMENT_QUERY from 'graphql/queries/userContent/EpubAnnotationsFromSharesInDocumentQuery.graphql';
import USER_CONTENT_QUERY from 'graphql/queries/userContent/UserContentQuery.graphql';
import ANNOTATIONS_NOT_IN_DOCUMENT_QUERY from 'graphql/queries/userContent/EpubAnnotationsNotInDocumentQuery.graphql';
import ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT_QUERY from 'graphql/queries/userContent/EpubAnnotationsFromSharesNotInDocumentQuery.graphql';
import { getIDBObjects } from 'utilities/idbUtilities';

/**
 * GraphQL operation names for the startup queries in this hook, in the order
 * they unblock each other while the session is online.
 *
 * During an offline mobile session `EpubAnnotationsNotInDocumentQuery` does not
 * wait for `UserContentQuery`. The React Native app answers these requests from
 * disk and switches the reader offline after this hook dispatches
 * MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT. These five names have to stay in
 * `readerStartupGraphqlOperations` in reader-mobile's `ReaderWebView.tsx`;
 * that list also contains other startup queries.
 *
 * `useQueryForUserContent.test.js` compares this constant to the queries
 * actually used in this hook, so adding, removing or renaming a query fails CI.
 *
 * See `RedShelfReader/docs/OFFLINE_OVERVIEW.md` in reader-mobile.
 */
export const STARTUP_USER_CONTENT_OPERATIONS = [
  'EpubAnnotationsInDocumentQuery',
  'EpubAnnotationsFromSharesInDocumentQuery',
  'UserContentQuery',
  'EpubAnnotationsNotInDocumentQuery',
  'EpubAnnotationsFromSharesNotInDocumentQuery',
];

const annotationKeys = {
  annotationsData: [
    'epubAnnotationsInDocument',
    'epubAnnotationsNotInDocument',
  ],
  annotationsFromSharesData: [
    'epubAnnotationsFromSharesInDocument',
    'epubAnnotationsFromSharesNotInDocument',
  ],
};

export default function useQueryForUserContent(book, initialDocumentIndex) {
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { isMobileApp, isMobileAppOffline } = useContext(
    MobileAppValuesContext,
  );
  const shouldBootstrapAllAnnotations = isMobileApp && isMobileAppOffline;
  // Boolean to determine if we should update the merged annotations in idb
  const [shouldUpdateAnnotations, setShouldUpdateAnnotations] = useState(false);
  const [shouldUpdateSharedAnnotations, setShouldUpdateSharedAnnotations] =
    useState(false);

  const setDataHelper = useCallback(
    (data) => {
      if (data) {
        setData((prevData) => ({ ...prevData, ...data }));
      }
    },
    [setData],
  );

  useEffect(
    function setDataFromIDB() {
      if (!book.pk) {
        return;
      }
      getIDBObjects(book.pk, 'graphql').then((idbObjects) => {
        if (idbObjects) {
          const { store } = idbObjects;
          Object.keys(annotationKeys).forEach((key) => {
            store
              .get(`${key} ${book.id}`)
              .then((data) => setDataHelper({ [key]: data }));
          });
        }
      });
    },
    [book.pk, book.id, setDataHelper],
  );

  const {
    data: annotationsInDocumentData,
    isFinished: annotationsInDocumentIsFinished,
    refetch: annotationsInDocumentRefetch,
  } = useQueryOffline(ANNOTATIONS_IN_DOCUMENT_QUERY, {
    variables: {
      epubId: book.id,
      documentIndex: initialDocumentIndex,
    },
    paginated: true,
    shouldUseMergedFields: true,
    onError: (error) => setError(error),
  });

  const {
    data: annotationsFromSharesInDocumentData,
    isFinished: annotationsFromSharesInDocumentIsFinished,
    refetch: annotationsFromSharesInDocumentRefetch,
  } = useQueryOffline(ANNOTATIONS_FROM_SHARES_IN_DOCUMENT_QUERY, {
    variables: {
      epubId: book.id,
      documentIndex: initialDocumentIndex,
    },
    skip: !annotationsInDocumentIsFinished,
    paginated: true,
    shouldUseMergedFields: true,
    onError: (error) => setError(error),
  });

  const {
    data: userContentData,
    isFinished: userContentIsFinished,
    refetch: userContentRefetch,
  } = useQueryOffline(USER_CONTENT_QUERY, {
    variables: {
      epubId: book.pk,
    },
    skip: !annotationsFromSharesInDocumentIsFinished,
    paginated: true,
    onError: (error) => setError(error),
  });

  const {
    data: annotationsNotInDocumentData,
    isFinished: annotationsNotInDocumentIsFinished,
    refetch: annotationsNotInDocumentRefetch,
  } = useQueryOffline(ANNOTATIONS_NOT_IN_DOCUMENT_QUERY, {
    variables: {
      epubId: book.id,
      documentIndex: initialDocumentIndex,
    },
    /**
     * During an offline mobile session this query starts without waiting for
     * `UserContentQuery`, because the app serves the whole chain from native
     * caches and keeps the reader online until the chain completes.
     *
     * That only holds while `isOffline` is false: `useQueryOffline` also skips on
     * `isOffline`, and a query skipped that way contributes nothing until it is
     * unskipped again. Hence the app waits for
     * MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT below instead of guessing from
     * request counts. See `RedShelfReader/docs/OFFLINE_OVERVIEW.md` in
     * reader-mobile.
     */
    skip: !userContentIsFinished && !shouldBootstrapAllAnnotations,
    paginated: true,
    shouldUseMergedFields: true,
    onError: (error) => setError(error),
  });

  const {
    data: annotationsFromSharesNotInDocumentData,
    isFinished: annotationsFromSharesNotInDocumentIsFinished,
    refetch: annotationsFromSharesNotInDocumentRefetch,
  } = useQueryOffline(ANNOTATIONS_FROM_SHARES_NOT_IN_DOCUMENT_QUERY, {
    variables: {
      epubId: book.id,
      documentIndex: initialDocumentIndex,
    },
    skip: !annotationsNotInDocumentIsFinished,
    paginated: true,
    shouldUseMergedFields: true,
    onError: (error) => setError(error),
  });

  useEffect(
    function setAnnotationsInDocumentData() {
      setDataHelper(annotationsInDocumentData);
      setShouldUpdateAnnotations(true);
    },
    [annotationsInDocumentData, setDataHelper],
  );

  useEffect(
    function setAnnotationsFromSharesInDocumentData() {
      setDataHelper(annotationsFromSharesInDocumentData);
      setShouldUpdateSharedAnnotations(true);
    },
    [annotationsFromSharesInDocumentData, setDataHelper],
  );

  useEffect(
    function setUserContentData() {
      setDataHelper(userContentData);
    },
    [userContentData, setDataHelper],
  );

  useEffect(
    function setAnnotationsNotInDocumentData() {
      setDataHelper(annotationsNotInDocumentData);
      setShouldUpdateAnnotations(true);
    },
    [annotationsNotInDocumentData, setDataHelper],
  );

  useEffect(
    function setAnnotationsFromSharesNotInDocumentData() {
      setDataHelper(annotationsFromSharesNotInDocumentData);
      setShouldUpdateSharedAnnotations(true);
    },
    [annotationsFromSharesNotInDocumentData, setDataHelper],
  );

  const mergeAnnotationData = useCallback(
    (data, key) => {
      getIDBObjects(book.pk, 'graphql', true).then((idbObjects) => {
        if (idbObjects) {
          const { store } = idbObjects;
          const annotations = annotationKeys[key].reduce(
            (acc, annotationKey) => {
              acc = acc.concat(data[annotationKey]?.edges ?? []);
              return acc;
            },
            [],
          );
          store.put(annotations, `${key} ${book.id}`);
          setDataHelper({ [key]: annotations });
        }
      });
    },
    [book.pk, book.id, setDataHelper],
  );

  useEffect(
    function setMergedAnnotationsData() {
      const latestAnnotationData = {
        ...data,
        ...annotationsInDocumentData,
        ...annotationsNotInDocumentData,
      };

      if (
        annotationsInDocumentIsFinished &&
        annotationsNotInDocumentIsFinished &&
        latestAnnotationData &&
        shouldUpdateAnnotations
      ) {
        mergeAnnotationData(latestAnnotationData, 'annotationsData');
        setShouldUpdateAnnotations(false);
      }
    },
    [
      annotationsInDocumentIsFinished,
      annotationsNotInDocumentIsFinished,
      annotationsInDocumentData,
      annotationsNotInDocumentData,
      data,
      mergeAnnotationData,
      shouldUpdateAnnotations,
    ],
  );

  useEffect(
    function setMergedAnnotationsFromSharesData() {
      const latestSharedAnnotationData = {
        ...data,
        ...annotationsFromSharesInDocumentData,
        ...annotationsFromSharesNotInDocumentData,
      };

      if (
        annotationsFromSharesInDocumentIsFinished &&
        annotationsFromSharesNotInDocumentIsFinished &&
        latestSharedAnnotationData &&
        shouldUpdateSharedAnnotations
      ) {
        mergeAnnotationData(
          latestSharedAnnotationData,
          'annotationsFromSharesData',
        );
        setShouldUpdateSharedAnnotations(false);
      }
    },
    [
      annotationsFromSharesInDocumentIsFinished,
      annotationsFromSharesNotInDocumentIsFinished,
      annotationsFromSharesInDocumentData,
      annotationsFromSharesNotInDocumentData,
      data,
      mergeAnnotationData,
      shouldUpdateSharedAnnotations,
    ],
  );

  const areAllQueriesCompleted =
    annotationsInDocumentIsFinished &&
    annotationsFromSharesInDocumentIsFinished &&
    userContentIsFinished &&
    annotationsNotInDocumentIsFinished &&
    annotationsFromSharesNotInDocumentIsFinished;

  useEffect(
    function announceStartupCompletionSupport() {
      if (!shouldBootstrapAllAnnotations) {
        return;
      }

      window[MOBILE_APP_STARTUP_COMPLETION_SUPPORT_FLAG] = true;
    },
    [shouldBootstrapAllAnnotations],
  );

  const hasSignaledStartupCompletion = useRef(false);

  /**
   * Every query above needs a second request returning zero edges before it
   * reports `isFinished`, so the mobile app cannot tell that the chain is done
   * by watching requests: switching the reader offline too early leaves
   * `isFinished` permanently false, because `useQueryOffline` skips the query
   * once `isOffline` is true. Announce completion instead.
   */
  useEffect(
    function signalStartupCompletionToMobileApp() {
      if (
        !shouldBootstrapAllAnnotations ||
        hasSignaledStartupCompletion.current ||
        !areAllQueriesCompleted ||
        shouldUpdateAnnotations ||
        shouldUpdateSharedAnnotations
      ) {
        return;
      }

      hasSignaledStartupCompletion.current = true;
      window.dispatchEvent(
        new CustomEvent(MOBILE_APP_STARTUP_QUERIES_COMPLETE_EVENT),
      );
    },
    [
      areAllQueriesCompleted,
      shouldBootstrapAllAnnotations,
      shouldUpdateAnnotations,
      shouldUpdateSharedAnnotations,
    ],
  );

  const userContentMap = useMemo(() => {
    return {
      annotationsInDocument: annotationsInDocumentRefetch,
      annotationsFromSharesInDocument: annotationsFromSharesInDocumentRefetch,
      userContent: userContentRefetch,
      annotationsNotInDocument: annotationsNotInDocumentRefetch,
      annotationsFromSharesNotInDocument:
        annotationsFromSharesNotInDocumentRefetch,
    };
  }, [
    annotationsInDocumentRefetch,
    annotationsFromSharesInDocumentRefetch,
    userContentRefetch,
    annotationsNotInDocumentRefetch,
    annotationsFromSharesNotInDocumentRefetch,
  ]);

  const refetchUserContent = useCallback(
    async (contentToRefetch = []) => {
      [...contentToRefetch]
        .sort((queryA, queryB) => queryA.order - queryB.order)
        .forEach(async (userContentType) => {
          if (userContentMap[userContentType.query]) {
            await userContentMap[userContentType.query]();
          }
        });
    },
    [userContentMap],
  );

  return {
    data,
    error,
    refetchUserContent,
    areAllQueriesCompleted,
  };
}
